import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import { ScenarioRequestSchema, ScenarioResponseSchema } from './ScenarioTypes';
import type { ScenarioRequestType, ScenarioResponseType } from './ScenarioTypes';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import type { AssignmentResponseType } from './AssignmentApi';
import { AssignmentResponseSchema } from './AssignmentApi';
import { features } from '../../shared/Features/features';
import { get } from 'svelte/store';
import {
    GetTargetStateResponseSchema,
    type GetTargetStateResponseType,
    TargetStageSchema,
    type TargetStageTypes,
} from './TargetStage';
import { AssignmentJobResponseSchema, AssignmentJobSuggestSchema } from './AssignmentJobApi';

/*
curl --request PUT \
  --url https://lm.nemo.indigo/api/scenario/250/tactical_plan_id \
  --header 'Content-Type: application/json' \
  --header 'Token: 40eb651811ed1209139dbef2ff4c8a97' \
  --header 'User-Agent: insomnia/2023.5.8' \
  --cookie PHPSESSID=2hmfv7avqiuccngurrvils8n80 \
  --data 8
można też użyć metody, która skorzysta z obecnie ustawionego:
curl --request POST \
  --url https://lm.nemo.indigo/api/scenario/250/create_assignments \
  --header 'Content-Type: application/json' \
  --header 'Token: 40eb651811ed1209139dbef2ff4c8a97' \
  --header 'User-Agent: insomnia/2023.5.8' \
  --cookie PHPSESSID=2hmfv7avqiuccngurrvils8n80 \
  --data '{}'
 */

export const SetTargetStageRequestSchema = z.object({
    assignment_ids: z.array(z.number()),
    stage_target: TargetStageSchema,
});
export type SetTargetStageRequestType = z.infer<typeof SetTargetStageRequestSchema>;

export const SetTargetStageResponseSchema = z.any();
export type SetTargetStageResponseType = z.infer<typeof SetTargetStageResponseSchema>;

export const GetBestAssignmentRequestSchema = z.object({
    assignment_ids: z.array(z.number()).optional(),
    job_id: z.number(),
    calc_routes: z.boolean(),
});
export type GetBestAssignmentRequestType = z.infer<typeof GetBestAssignmentRequestSchema>;

// {"nearest":[{"assignment_id":10,"coords":{"x":20.922974,"y":52.24906},"distance":0,"bestfit":{"index":2,"existing":true,"compare":{"distance_m":0,"eta_m":0}}},{"assignment_id":1,"coords":{"x":20.91871,"y":52.24596},"distance":451.1591672370195,"bestfit":{"index":1,"existing":false,"compare":{"distance_m":6440,"eta_m":23}}},{"assignment_id":2,"coords":{"x":20.91871,"y":52.24596},"distance":451.1591672370195,"bestfit":{"index":1,"existing":false,"compare":{"distance_m":6440,"eta_m":23}}},{"assignment_id":3,"coords":{"x":20.91871,"y":52.24596},"distance":451.1591672370195,"bestfit":{"index":1,"existing":false,"compare":{"distance_m":6440,"eta_m":23}}},{"assignment_id":4,"coords":{"x":20.91871,"y":52.24596},"distance":451.1591672370195,"bestfit":{"index":1,"existing":false,"compare":{"distance_m":6440,"eta_m":23}}}],"blocked":[]}
export const GetBestAssignmentResponseSchema = z.object({
    nearest: z.array(
        z.object({
            assignment_id: z.number(),
            coords: z.object({ x: z.number(), y: z.number() }),
            distance: z.number(),
            bestfit: z
                .object({
                    index: z.number(),
                    existing: z.boolean(),
                    compare: z.object({
                        distance_m: z.number(),
                        eta_m: z.number(),
                    }),
                })
                .nullable()
                .catch(null),
        })
    ),
    blocked: z.array(z.number()),
});
export type GetBestAssignmentResponseType = z.infer<typeof GetBestAssignmentResponseSchema>;

export const ScenarioToCompareResponseSchema = z.object({
    scenario: ScenarioResponseSchema,
    assignments: z.array(AssignmentResponseSchema),
    suggested_assignment_ids: z.array(AssignmentJobSuggestSchema),
});
export type ScenarioToCompareResponseType = z.infer<typeof ScenarioToCompareResponseSchema>;

export class ScenarioApi {
    public static async changeTacticalPlan(
        api: Api,
        scenarioId: number,
        tacticalPlanId: number
    ): Promise<ScenarioResponseType> {
        return api.patch(
            `/scenario/${scenarioId}`, // + `/tactical_plan_id',
            'replace',
            '/tactical_plan_id',
            tacticalPlanId,
            ScenarioResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        id: number,
        req: ScenarioRequestType
    ): Promise<ScenarioResponseType> {
        return api.getData<ScenarioRequestType, ScenarioResponseType>(
            'put',
            `/scenario/${id}`,
            req,
            ScenarioRequestSchema,
            ScenarioResponseSchema
        );
    }

    public static async create(api: Api, req: ScenarioRequestType): Promise<ScenarioResponseType> {
        return api.getData<ScenarioRequestType, ScenarioResponseType>(
            'post',
            '/scenario',
            req,
            ScenarioRequestSchema,
            ScenarioResponseSchema
        );
    }

    public static async getAll(api: Api) {
        return api.getData<undefined, ScenarioResponseType[]>(
            'get',
            '/scenarios' + new ApiFilterBuilder(null).sortBy('day', 'asc').getUri(),
            undefined,
            z.undefined(),
            z.array(ScenarioResponseSchema)
        );
    }

    public static async getOne(api: Api, id: number): Promise<ScenarioResponseType> {
        return api.getData<undefined, ScenarioResponseType>(
            'get',
            `/scenario/${id}`,
            undefined,
            z.undefined(),
            ScenarioResponseSchema
        );
    }

    public static async getOneForDay(api: Api, day: string) {
        let scenarios = await ScenarioApi.getAllForDay(api, day);
        return scenarios[0];
    }

    public static async getAllForDay(api: Api, day: string) {
        let f = new ApiFilterBuilder(null).filterByString('day', day);
        return api.getData<undefined, ScenarioResponseType[]>(
            'get',
            '/scenarios' + f.getUri(),
            undefined,
            z.undefined(),
            z.array(ScenarioResponseSchema)
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<ScenarioResponseType[]> {
        return api.getData<undefined, ScenarioResponseType[]>(
            'get',
            `/scenarios` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(ScenarioResponseSchema)
        );
    }

    public static async setStartPlanning(
        api: Api,
        id: number,
        started: boolean
    ): Promise<ScenarioResponseType> {
        return api.patch(
            `/scenario/${id}`,
            'replace',
            '/planning_started',
            started ? new Date().toISOString() : null,
            ScenarioResponseSchema
        );
    }

    // see: README_feature_new_set_pt_logic.MD
    public static async resetAssignments(
        api: Api,
        id: number,
        tactical_plan_id: number
    ): Promise<ScenarioResponseType> {
        return api.getData(
            'post',
            `/scenario/${id}/reset_assignments`,
            { tactical_plan_id },
            z.object({ tactical_plan_id: z.number() }),
            ScenarioResponseSchema
        );
    }

    public static async startRouting(
        api: Api,
        scenarioId: number,
        assignmentIds: number[]
    ): Promise<any> {
        let req: any = { id: {} };
        if (assignmentIds.length === 0) {
            req = {};
        } else {
            assignmentIds.forEach(id => (req.id[id.toString()] = false));
        }
        return api.getData<any, any>(
            'post',
            `/scenario/${scenarioId}/start_routing`,
            req,
            z.any(),
            z.any()
        );
    }

    public static async getBestAssignment(
        api: Api,
        scenarioId: number,
        jobId: number,
        assignmentIds: number[] = [],
        calcRoutes: boolean = false
    ): Promise<GetBestAssignmentResponseType> {
        let req: GetBestAssignmentRequestType = { job_id: jobId, calc_routes: calcRoutes };
        if (assignmentIds.length > 0) {
            req.assignment_ids = assignmentIds;
        }
        return api.getData<GetBestAssignmentRequestType, GetBestAssignmentResponseType>(
            'post',
            `/scenario/${scenarioId}/get_best_assignment`,
            req,
            GetBestAssignmentRequestSchema,
            GetBestAssignmentResponseSchema
        );
    }

    public static async getAdHocAssignment(
        api: Api,
        scenarioId: number
    ): Promise<AssignmentResponseType> {
        return api.getData<any, AssignmentResponseType>(
            'post',
            `/scenario/${scenarioId}/get_ad_hoc_assignment`,
            {},
            z.object({}).catch({}),
            AssignmentResponseSchema // FIXME: add catch for empty array if no results (php issue)
        );
    }

    public static async setAsSelected(
        api: Api,
        scenarioId: number
    ): Promise<AssignmentResponseType> {
        return api.getData<any, AssignmentResponseType>(
            'post',
            `/scenario/${scenarioId}/set_as_selected`,
            {},
            z.object({}).catch({}),
            ScenarioResponseSchema
        );
    }

    public static async setTargetStage(
        api: Api,
        stage_target: TargetStageTypes,
        assignment_ids: number[]
    ): Promise<SetTargetStageResponseType> {
        return api.getData<SetTargetStageRequestType, SetTargetStageResponseType>(
            'post',
            `/scenario/set_target_stage`,
            { stage_target, assignment_ids },
            SetTargetStageRequestSchema,
            SetTargetStageResponseSchema
        );
    }

    public static async resetTargetStage(api: Api, scenarioId: number): Promise<any> {
        return api.getData<any, any>(
            'post',
            '/scenario/' + scenarioId + '/reset_target_stage',
            {},
            z.object({}),
            z.any()
        );
    }

    public static async getTargetStage(
        api: Api,
        scenarioId: number
    ): Promise<GetTargetStateResponseType> {
        return api.getData<any, GetTargetStateResponseType>(
            'post',
            '/scenario/' + scenarioId + '/get_target_stage',
            {},
            z.object({}),
            GetTargetStateResponseSchema
        );
    }

    public static async runStageProgress(api: Api, singleStep: boolean = true): Promise<any> {
        let endpoint = singleStep
            ? '/scenario/run_stage_progress_single_step'
            : '/scenario/run_stage_progress';
        return api.getData<any, any>('post', endpoint, {}, z.object({}), z.any());
    }

    public static async initScenarioToCompare(
        api: Api,
        scenario_id: number,
        tactical_plan_id: number
    ): Promise<any> {
        return api.getData<{ scenario_id: number; tactical_plan_id: number }, any>(
            'post',
            `/scenario/init_scenario_to_compare`,
            { scenario_id, tactical_plan_id },
            z.object({ scenario_id: z.number(), tactical_plan_id: z.number() }),
            z.any()
        );
    }

    // public static async getScenarioToCompare(
    //     api: Api,
    //     scenarioId: number
    // ): Promise<ScenarioToCompareResponseType> {
    //     return api.getData<{ scenario_id: number }, ScenarioToCompareResponseType>(
    //         'post',
    //         `/scenario/get_scenario_to_compare`,
    //         { scenario_id: scenarioId },
    //         z.object({ scenario_id: z.number() }),
    //         ScenarioToCompareResponseSchema
    //     );
    // }

    public static async checkScenarioToCompare(
        api: Api,
        scenarioId: number
    ): Promise<{ assignmentsInCalculation: number[] }> {
        return api.getData<{ scenario_id: number }, { assignmentsInCalculation: number[] }>(
            'post',
            `/scenario/check_scenario_to_compare`,
            { scenario_id: scenarioId },
            z.object({ scenario_id: z.number() }),
            z.object({ assignmentsInCalculation: z.array(z.number()) })
        );
    }

    public static async getAssignmentJobsToCompare(
        api: Api,
        scenarioId: number,
        options: { assignment_id?: number }
    ): Promise<{ assignmentsInCalculation: number[] }> {
        return api.getData<{ scenario_id: number }, { assignmentsInCalculation: number[] }>(
            'post',
            `/scenario/get_saved_state`,
            { scenario_id: scenarioId, types: ['assignment_job'], ...options },
            z.object({ scenario_id: z.number(), assignment_id: z.number().optional(), types: z.array(z.string()).optional() }),
            z.array(AssignmentJobResponseSchema)
        );
    }
}
